import { API_URL } from '../config/config';
import { getRequest } from '../utils/axiosUtils';
import buildQueryParams from '../utils/buildQueryParams';

/**
 * Bot Coverage report data.
 *
 * GET /admin/analytics/stat/getBotCoverage — one page of the members we are not
 * reaching with bots, plus population totals, city gaps and plan breakdown, all
 * computed over the whole filtered set server-side (never the page slice).
 *
 * The three scopes are disjoint and every member falls in exactly one of them
 * (the fourth, `covered`, is the complement the tab does not list):
 *   norange     no bot within `radius` km of them — a coverage gap
 *   nomessage   a bot IS in range and they have still never had a bot message
 *   nolocation  no usable coordinates on either location store, so no proximity
 *               search can place them at all
 *
 * `radius` is an explicit admin control because there is no per-member radius to
 * read: `preferences.distance` is unset on every member in the database, and the
 * app takes its search radius from the client request. `audience` chooses between
 * Active members — approved + hidden, every completed profile — (`active`, the default;
 * the server also accepts `approved` for on-search-only) and every non-bot member (`all`).
 *
 * The response carries `warming: true` for the first minutes after a server
 * restart, while the population snapshot is still being built. That is NOT an
 * error and must not render as zeros — the tab shows a building state instead.
 */
export const getBotCoverage = async (filter = {}) => {
  const {
    scope, audience, radius, gender, plan, country, city, searchTerm,
    joined, from, to, verified, sort, page, limit,
  } = filter;

  const qp = buildQueryParams({ country, city, searchTerm, limit });
  const extra = [];
  if (scope) extra.push(`scope=${encodeURIComponent(scope)}`);
  if (audience) extra.push(`audience=${encodeURIComponent(audience)}`);
  if (radius) extra.push(`radius=${encodeURIComponent(radius)}`);
  if (gender) extra.push(`gender=${encodeURIComponent(gender)}`);
  if (plan) extra.push(`plan=${encodeURIComponent(plan)}`);
  // `joined` scopes which members are listed, by signup date; with joined=custom it
  // carries an inclusive from/to (YYYY-MM-DD, read as IST days server-side).
  if (joined) extra.push(`joined=${encodeURIComponent(joined)}`);
  if (from) extra.push(`from=${encodeURIComponent(from)}`);
  if (to) extra.push(`to=${encodeURIComponent(to)}`);
  if (verified) extra.push('verified=true');
  if (sort) extra.push(`sort=${encodeURIComponent(sort)}`);
  if (page) extra.push(`page=${encodeURIComponent(page)}`);
  const query = [qp, extra.join('&')].filter(Boolean).join('&');

  const res = await getRequest({
    url: `${API_URL}/admin/analytics/stat/getBotCoverage${query ? `?${query}` : ''}`,
    isAbort: false,
  });
  if (!res) return null;
  if (res.status >= 200 && res.status < 300 && res.data) return res.data.data;
  throw new Error(`Unexpected response code: ${res.status}`);
};

export default getBotCoverage;
